import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigation } from '../../contexts/NavigationContext';

type MarkdownContentProps = {
  content: string;
  className?: string;
  renderText?: (text: string) => React.ReactNode;
  /** Known wiki titles for unresolved link detection */
  knownWikiTitles?: Set<string>;
  /** Called when user clicks an unresolved link's "Create" action */
  onCreateArticle?: (title: string) => void;
};

export default function MarkdownContent({ content, className, renderText, knownWikiTitles, onCreateArticle }: MarkdownContentProps) {
  const navigation = useNavigation();

  if (!content) return <span className="text-[var(--text-muted)]">—</span>;

  // Transform [[Wiki Links]] to standard markdown links with a custom scheme
  // Pattern: [[Display Text]] -> [Display Text](wiki:Display%20Text)
  const processedContent = content.replace(/\[\[(.*?)\]\]/g, (_, text) => {
    return `[${text}](wiki:${encodeURIComponent(text)})`;
  });

  // Helper to recursively process React children and apply renderText to string nodes
  const processChildren = (children: React.ReactNode): React.ReactNode => {
    if (!renderText) return children;
    if (typeof children === 'string') return renderText(children);
    if (Array.isArray(children)) return children.map((c, i) => {
      if (typeof c === 'string') return <span key={i}>{renderText(c)}</span>;
      return c;
    });
    return children;
  };

  return (
    <div className={`markdown-content ${className || ''}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          ...(renderText ? {
            p: ({ children, ...props }) => <p {...props}>{processChildren(children)}</p>,
            li: ({ children, ...props }) => <li {...props}>{processChildren(children)}</li>,
            td: ({ children, ...props }) => <td {...props}>{processChildren(children)}</td>,
            th: ({ children, ...props }) => <th {...props}>{processChildren(children)}</th>,
            strong: ({ children, ...props }) => <strong {...props}>{processChildren(children)}</strong>,
            em: ({ children, ...props }) => <em {...props}>{processChildren(children)}</em>,
          } : {}),
          a: ({ href, children, ...props }) => {
            const isWikiLink = href?.startsWith('wiki:');
            
            if (isWikiLink && navigation) {
              const target = decodeURIComponent(href!.replace('wiki:', ''));
              const isResolved = !knownWikiTitles || knownWikiTitles.has(target.toLowerCase());

              if (!isResolved) {
                return (
                  <span
                    className="inline-flex items-center gap-0.5 font-medium text-[var(--danger)] cursor-help border-b border-dashed border-[var(--danger)]"
                    title={`"${target}" not found`}
                  >
                    {children}
                    {onCreateArticle && (
                      <a
                        href="#"
                        className="text-[10px] text-[var(--accent-text)] hover:text-[var(--accent)] ml-0.5 no-underline"
                        onClick={(e) => {
                          e.preventDefault();
                          onCreateArticle(target);
                        }}
                        title={`Create "${target}"`}
                      >
                        [+]
                      </a>
                    )}
                  </span>
                );
              }

              return (
                <a
                  {...props}
                  href="#"
                  className="font-medium text-[var(--accent-text)] hover:text-[var(--accent)] hover:underline decoration-[var(--accent-muted)] cursor-pointer transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    navigation.navigateTo(target);
                  }}
                  title={`Go to ${target}`}
                >
                  {children}
                </a>
              );
            }

            return (
              <a {...props} href={href} className="markdown-link" target="_blank" rel="noreferrer">
                {children}
              </a>
            );
          },
          code: (props) => {
            const { inline, children, ...rest } = props as {
              inline?: boolean;
              children?: React.ReactNode;
            } & React.HTMLAttributes<HTMLElement>;
            return inline ? (
              <code {...rest} className="markdown-inline-code">
                {children}
              </code>
            ) : (
              <pre className="markdown-code">
                <code {...rest}>{children}</code>
              </pre>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
